import { useState, useEffect } from "react";
import PageLayout from "@/components/custom/PageLayout";
import { Label } from "@/components/ui/label";
import CarLeft from "@/assets/illustrations/CarLeft.png";
import SuccessTickIcon from "@/assets/icons/SuccessTickIcon.svg";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { useTheme } from "@/themeConfigs/ThemeContext";
import { CreateRosterReq } from "@/central-atoms/everest-fleet-pwa/requests";
import { ChevronLeftIcon } from "lucide-react";
import RosterFlowService from "@/services/api/RosterFlowService";

const Roster: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [eligibility, setEligibility] = useState({
    liveAllowed: false,
    weeklyAllowed: false,
    shiftTimeAllowed: false,
    liveRosterMessage: "",
    weeklyRosterMessage: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [rosterType, setRosterType] = useState("live");
  const [selectedDayOff, setSelectedDayOff] = useState<string | null>(null);
  const [selectedShiftTime, setSelectedShiftTime] = useState<string | null>(
    null
  );
  const [workToday, setWorkToday] = useState<string>("Yes");
  const [daysOffs, setDaysOffs] = useState<string[]>([]);
  const [shiftTimes, setShiftTimes] = useState<string[]>([]);
  const [rosterCreated, setRosterCreated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchEligibility = () => {
      RosterFlowService.rosterEligibility()
        .then((response) => {
          setEligibility({
            liveAllowed: response.data.live_allowed,
            weeklyAllowed: response.data.weekly_allowed,
            shiftTimeAllowed: response.data.shift_time_allowed,
            liveRosterMessage: response.data.live_roster_message || "",
            weeklyRosterMessage: response.data.weekly_roster_message || "",
          });
        })
        .catch((err) => {
          setEligibility((prev) => ({
            ...prev,
            liveAllowed: err?.response?.data.live_allowed,
            weeklyAllowed: err?.response?.data.weekly_allowed,
            liveRosterMessage: err?.response?.data.live_roster_message,
            weeklyRosterMessage: err?.response?.data?.weekly_roster_message,
            shiftTimeAllowed: err?.response?.data.shift_time_allowed,
          }));
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchEligibility();
  }, []);


  const handleRosterTypeChange = async (type: string) => {
    setRosterType(type);
    setError(null);

    if (type === "live") {
      setSelectedDayOff(null);
      setSelectedShiftTime(null);
      setWorkToday("Yes");
    } else if (eligibility.weeklyAllowed) {
      try {
        const response = await RosterFlowService.rosterAvailability(type);

        if (!response.success) {
          setError(response.message);
          setDaysOffs([]);
          setShiftTimes([]);
          return;
        }

        const daysOffs = [
          { title: "Full week off" },
          ...response.data.day_offs,
        ].map((day) => day.title);

        const shiftTimes = response.data.shift_times.map(
          (shift: any) => shift.title
        );

        setDaysOffs(daysOffs);
        setShiftTimes(shiftTimes);
      } catch (err: any) {
        setAvailabilityError(err.response.data.message);
      }
    }
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (rosterType === "live") {
        if (workToday === "No") return;

        const reqBody = { type: rosterType };

        await RosterFlowService.createRoster(reqBody);
        setRosterCreated(true);
      } else {
        if (rosterType === "weekly" && !selectedDayOff) {
          setError("Please select a day off.");
          setIsSubmitting(false);
          return;
        }

        if (eligibility.shiftTimeAllowed && !selectedShiftTime) {
          setError("Please select a shift time.");
          setIsSubmitting(false);
          return;
        }

        const reqBody: CreateRosterReq = {
          type: rosterType,
          ...(selectedDayOff && { day_off: selectedDayOff }),
          ...(selectedShiftTime && { shift_time: selectedShiftTime }),
        };

        await RosterFlowService.createRoster(reqBody);
        setRosterCreated(true);
      }
    } catch (err: any) {
      setError("Failed to create roster. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Oval
          height={70}
          width={70}
          color={theme.colors.primary}
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor={theme.colors.primary}
          strokeWidth={5}
          strokeWidthSecondary={2}
        />
      </div>
    );
  }

  if (rosterCreated) {
    return (
      <PageLayout bgColor="bg-[#e4fde5] items-center">
        <div className="flex flex-col items-center justify-center">
          <img src={SuccessTickIcon} alt="Success" />
          <p className="text-3xl text-center font-bold">Roster</p>
          <p className="text-3xl text-center font-bold">Successfully Created</p>
          <Button
            variant="secondary"
            className="w-9/12 mt-4"
            onClick={() => navigate("/driver-home")}
          >
            Back to Home
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout noPad>
      <div className="bg-secondary">
        <div className="w-full flex justify-between pt-10">
          <Button
            variant="icon"
            className="text-white bg-secondary rounded-xl border-2 border-white hover:shadow-lg px-2 py-6 ml-4 mb-6"
            onClick={() => navigate("/driver-home")}
          >
            <ChevronLeftIcon strokeWidth={3} size={28} />
          </Button>
        </div>
        <div className="w-full flex justify-start">
          <img src={CarLeft} className="w-1/2" alt="Car Left" />
        </div>
        <p className="flex flex-row justify-center text-4xl text-white py-10">
          Roster
        </p>
      </div>
      <div className="bg-white px-7">
        <div className="flex flex-col space-y-4">
          <div>
            <Label
              className="block text-sm font-bold py-2"
              htmlFor="roster-type"
            >
              Roster Type
            </Label>
            <Select value={rosterType} onValueChange={handleRosterTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select roster type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rosterType === "live" && (
            <div>
              <p className="text-red-500 text-sm">
                {!eligibility.liveAllowed && eligibility.liveRosterMessage}
              </p>
              <Label
                className="block text-sm font-bold py-2"
                htmlFor="work-today"
              >
                Want to work today?
              </Label>
              <Select
                value={workToday}
                onValueChange={setWorkToday}
                disabled={!eligibility.liveAllowed}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>{workToday}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {rosterType === "weekly" && (
            <>
              {!eligibility.weeklyAllowed && (
                <p className="text-red-500 text-sm">
                  {eligibility.weeklyRosterMessage}
                </p>
              )}
              <div>
                <Label
                  className="block text-sm font-bold py-2"
                  htmlFor="day-off"
                >
                  Please Select Your Day Off
                </Label>
                <Select
                  value={selectedDayOff as string}
                  onValueChange={setSelectedDayOff}
                  disabled={!eligibility.weeklyAllowed}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your day off" />
                  </SelectTrigger>
                  <SelectContent>
                    {daysOffs.map((day, index) => (
                      <SelectItem key={index} value={day}>
                        {day === "Full week off" ? day : `Day off: ${day}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {availabilityError && <p className="text-red-500 text-sm">{availabilityError}</p>}

              {eligibility.shiftTimeAllowed && (
                <div>
                  <Label
                    className="block text-sm font-bold py-2"
                    htmlFor="shift-time"
                  >
                    Please Select Your Shift Time
                  </Label>
                  <Select
                    value={selectedShiftTime as string}
                    onValueChange={setSelectedShiftTime}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select your shift time" />
                    </SelectTrigger>
                    <SelectContent>
                      {shiftTimes.map((shift, index) => (
                        <SelectItem key={index} value={shift}>
                          {shift}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="pt-48">
            <Button
              className="mt-7"
              variant="secondary"
              size="lg"
              onClick={handleConfirm}
              disabled={
                isSubmitting ||
                (rosterType === "live" && !eligibility.liveAllowed) ||
                (rosterType === "weekly" && !eligibility.weeklyAllowed)
              }
            >
              {isSubmitting ? (
                <Oval
                  height={24}
                  width={24}
                  color="white"
                  ariaLabel="button-loading"
                  secondaryColor={theme.colors.primary}
                  strokeWidth={4}
                  strokeWidthSecondary={2}
                />
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Roster;
